import React, {useMemo} from 'react';
import {useTranslation} from 'next-i18next';

import {getEverywhereCountry} from '@constants/countryList';
import type {SelectOption, SelectProps} from '@components/Select';
import Select from '@components/Select';
import type {CountryList} from '@components/Filters/types';
import {useLocalizedFormat} from '@deezer/next/contexts/localization';

type CountryFilterProps = {
	isLoading?: boolean;
	countryList: CountryList;
	value: string;
	defaultValue?: SelectOption;
	onChange: (countryCode: string) => void;
} & Omit<SelectProps, 'options' | 'value' | 'defaultValue' | 'onChange'>;

// TODO unify this component with the country filter on charts
const CountryFilter = ({
	isLoading,
	countryList,
	value,
	onChange,
	defaultValue,
	...rest
}: CountryFilterProps) => {
	const {t} = useTranslation('d4c');
	const {formatCountry} = useLocalizedFormat();

	const options = useMemo<SelectOption[]>(
		() =>
			countryList.map(country => {
				let label =
					formatCountry(country.countryCode.toUpperCase()) || country.label;

				if (country.countryCode === 'WW' || country.countryCode === 'ww') {
					label = t('analyticslabel_text_worldwide_d4c-web');
				}

				return {
					value: country.countryCode.toUpperCase(),
					label: country.countryCode !== label ? label : country.label,
				};
			}),
		[countryList, formatCountry, t],
	);

	const normalizedValue = useMemo(() => value?.toUpperCase() || 'WW', [value]);

	const noFilterDataFound = useMemo(
		() => options.length === 0 && !isLoading,
		[options.length, isLoading],
	);

	const fallbackDefaultValue =
		defaultValue?.value ??
		getEverywhereCountry(t)?.countryCode?.toUpperCase() ??
		'WW';

	return (
		<Select
			isDisabled={noFilterDataFound}
			options={options}
			value={normalizedValue}
			defaultValue={fallbackDefaultValue}
			onChange={onChange}
			placeholder={
				noFilterDataFound
					? t('playlistreport_text_nofilteravailable_d4c-web')
					: t('charts_title_country_d4c-web')
			}
			{...rest}
		/>
	);
};

export default CountryFilter;
